import { useEffect, useState } from "react";

import {
  difference,
  filter,
  flatMap,
  intersection,
  isEmpty,
  keys,
  map,
} from "lodash/fp";

import type { UseRecommendations } from "@app/analytics/hooks/recommendations/useRecommendations";
import { RECOMMENDATION_ACTION_FILTERS } from "@app/analytics/Recommendations/constants";
import type {
  RecommendationFilterType,
  RecommendationTabId,
} from "@app/analytics/Recommendations/types";
import { RecommendationCategoryType } from "@app/analytics/Recommendations/types";
import {
  getCountsByFilterType,
  getGroupedRecommendationIndexById,
} from "@app/analytics/Recommendations/utils";
import type { Recommendation } from "@app/analytics/types";
import * as RecommendationUtils from "@app/analytics/utils/recommendationUtils";
import usePagination from "@app/common/hooks/usePagination";

const filterRecommendations = (
  recommendations: Recommendation[],
  appliedFilters: RecommendationFilterType[]
) => {
  if (isEmpty(appliedFilters)) return recommendations;

  const actionKindsForFilterType = flatMap(
    "actionKinds",
    filter((f) => appliedFilters.includes(f.id), RECOMMENDATION_ACTION_FILTERS)
  );

  return filter((rec) => {
    const recommendationActionKinds = map("kind", rec.actions);
    return !isEmpty(
      intersection(recommendationActionKinds, actionKindsForFilterType)
    );
  }, recommendations);
};

const getVisibleReviewedRecommendationCategories = (
  allRecsByCategory: UseRecommendations["reviewedRecommendationsByCategory"]
) => {
  return filter(
    (category) =>
      !isEmpty(allRecsByCategory[category as RecommendationCategoryType]),
    keys(allRecsByCategory)
  ) as RecommendationCategoryType[];
};

const useFilteredRecommendations = (
  recommendations: UseRecommendations,
  selectedTab: RecommendationTabId
) => {
  // order is irrelevant
  const [appliedFilters, setAppliedFilters] = useState<
    RecommendationFilterType[]
  >([]);

  const [
    filteredGroupedPendingRecommendations,
    setFilteredGroupedPendingRecommendations,
  ] = useState(recommendations.groupedPendingRecommendations);

  const [
    filteredReviewedRecommendationsByCategory,
    setFilteredReviewedRecommendationsByCategory,
  ] = useState(recommendations.reviewedRecommendationsByCategory);

  const pendingPagination = usePagination(
    filteredGroupedPendingRecommendations,
    { pageSize: 1 }
  );

  const visibleReviewedRecommendationCategories =
    getVisibleReviewedRecommendationCategories(
      filteredReviewedRecommendationsByCategory
    );

  const reviewedPagination = usePagination(
    visibleReviewedRecommendationCategories,
    { pageSize: 1 }
  );

  const visibleRecommendations =
    selectedTab === "new"
      ? recommendations.pendingRecommendations
      : recommendations.completedRecommendations;

  const filterCounts = getCountsByFilterType(visibleRecommendations);

  useEffect(() => {
    if (isEmpty(appliedFilters)) return;

    // when switching tabs, update filters to only include those that are available on the new tab
    const filtersWithResults = keys(filterCounts) as RecommendationFilterType[];
    setAppliedFilters(intersection(appliedFilters, filtersWithResults));
  }, [selectedTab]);

  const updateRecommendationsFromFilters = (
    filters: RecommendationFilterType[]
  ) => {
    // determine which recommendation is currently selected and update the pagination so that
    //    the same recommendation is selected (if it appears in the updated filtered view), even
    //    if it's on a different "page" now. do this for both pending and reviewed recommendations
    const currentSelectedPendingRecommendation =
      filteredGroupedPendingRecommendations[pendingPagination.currentPage];

    const filteredPendingRecommendations = filterRecommendations(
      recommendations.pendingRecommendations,
      filters
    );
    const updatedPendingRecommendations =
      RecommendationUtils.groupRecommendations(filteredPendingRecommendations);

    const newPendingRecPage = getGroupedRecommendationIndexById(
      updatedPendingRecommendations,
      currentSelectedPendingRecommendation?.id
    );

    const updatedReviewedRecommendations = filterRecommendations(
      recommendations.completedRecommendations,
      filters
    );
    const updatedReviewedRecommendationsByCategory =
      RecommendationUtils.groupReviewedRecommendations(
        updatedReviewedRecommendations
      );

    const currentSelectedReviewedRecommendationCategory =
      visibleReviewedRecommendationCategories[reviewedPagination.currentPage];
    const updatedCategories = getVisibleReviewedRecommendationCategories(
      updatedReviewedRecommendationsByCategory
    );

    const newReviewedRecPage = Math.max(
      0,
      updatedCategories.indexOf(currentSelectedReviewedRecommendationCategory)
    );

    setAppliedFilters(filters);
    setFilteredGroupedPendingRecommendations(updatedPendingRecommendations);
    setFilteredReviewedRecommendationsByCategory(
      updatedReviewedRecommendationsByCategory
    );

    if (pendingPagination.currentPage !== newPendingRecPage) {
      pendingPagination.goToPage(newPendingRecPage);
    }
    if (reviewedPagination.currentPage !== newReviewedRecPage) {
      reviewedPagination.goToPage(newReviewedRecPage);
    }
  };

  const handleClickFilter = (filter: RecommendationFilterType) => () => {
    // 1. get the updated list of filters based on which chip was just toggled
    let newFilters: RecommendationFilterType[] = [];

    if (filter !== "all") {
      if (appliedFilters.includes(filter)) {
        newFilters = difference(appliedFilters, [filter]);
      } else {
        newFilters = [...appliedFilters, filter];

        if (newFilters.length === keys(filterCounts).length) {
          // switch to auto-select "All Results" when all filter types are selected
          newFilters = [];
        }
      }
    }

    updateRecommendationsFromFilters(newFilters);
  };

  useEffect(() => {
    updateRecommendationsFromFilters(appliedFilters);
  }, [
    recommendations.pendingRecommendations,
    recommendations.completedRecommendations,
    appliedFilters,
  ]);

  return {
    appliedFilters,
    filterCounts,
    filteredGroupedPendingRecommendations,
    filteredReviewedRecommendationsByCategory,
    onClickFilter: handleClickFilter,
    pendingPagination,
    reviewedPagination,
    reviewedRecommendationCategories: visibleReviewedRecommendationCategories,
  };
};

export default useFilteredRecommendations;
